<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useOperationsStore } from '@/stores/operations'
import { useUiStore } from '@/stores/ui'
import { LOCALE_LABELS, SUPPORTED_LOCALES, currentLocale, setLocale, type AppLocale } from '@/i18n'
import OBadge from '@/components/ui/OBadge.vue'
import OButton from '@/components/ui/OButton.vue'

/**
 * The application frame: brand, navigation, session menu, theme.
 *
 * The navigation is flat and short on purpose. Every section here maps onto one API resource, and
 * that is not a coincidence — the dashboard is an ordinary client of the public API (design §8.2),
 * so a screen that does not correspond to something the API exposes would be a screen built on a
 * privileged channel that must not exist.
 */

const ui = useUiStore()
const auth = useAuthStore()
const operations = useOperationsStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()

/**
 * The sections, named by catalogue key rather than by literal.
 *
 * Looked up at render time, so switching locale relabels the navigation in place instead of
 * leaving the previous language frozen in a `const`.
 */
const links = [
  { name: 'home', labelKey: 'nav.home', hintKey: 'nav.homeHint' },
  { name: 'assets', labelKey: 'nav.assets', hintKey: 'nav.assetsHint' },
  { name: 'operations', labelKey: 'nav.operations', hintKey: 'nav.operationsHint' },
  { name: 'activity', labelKey: 'nav.activity', hintKey: 'nav.activityHint' },
  { name: 'alerts', labelKey: 'nav.alerts', hintKey: 'nav.alertsHint' },
] as const

const activeCount = computed(() => operations.active.length)

const themeLabel = computed(() => t(`theme.${ui.theme}`))

const locale = computed({
  get: () => currentLocale(),
  set: (value: AppLocale) => setLocale(value),
})

const localeOptions = SUPPORTED_LOCALES.map((code) => ({ code, label: LOCALE_LABELS[code] }))

const signOut = async (): Promise<void> => {
  await auth.logout()
  await router.push({ name: 'login' })
}
</script>

<template>
  <div class="shell" :class="{ 'sidebar-open': ui.sidebarOpen }">
    <a class="skip" href="#main">{{ t('nav.skipToContent') }}</a>

    <aside class="sidebar">
      <div class="brand">
        <img class="mark" src="/oui-logo.svg" alt="" width="26" height="28" />
        <span class="wordmark">
          <strong>oui</strong> Observe
        </span>
      </div>

      <nav :aria-label="t('nav.sections')">
        <RouterLink
          v-for="link in links"
          :key="link.name"
          :to="{ name: link.name }"
          class="nav-link"
          :class="{ active: route.name === link.name || route.meta.section === link.name }"
          @click="ui.closeSidebar()"
        >
          <span class="nav-label">{{ t(link.labelKey) }}</span>
          <OBadge v-if="link.name === 'operations' && activeCount > 0" state="running">
            {{ activeCount }}
          </OBadge>
          <span v-else class="nav-hint">{{ t(link.hintKey) }}</span>
        </RouterLink>
      </nav>

      <div class="sidebar-foot">
        <label class="locale">
          <span class="sr-only">{{ t('locale.label') }}</span>
          <select v-model="locale" :aria-label="t('locale.switch')">
            <option v-for="option in localeOptions" :key="option.code" :value="option.code">
              {{ option.label }}
            </option>
          </select>
        </label>

        <button type="button" class="theme" :aria-label="t('theme.cycle')" @click="ui.cycleTheme()">
          {{ themeLabel }}
        </button>
        <a
          class="tiny docs"
          href="https://oui.onl/fr/documentation/api/oui-observer-api"
          target="_blank"
          rel="noopener"
        >{{ t('nav.documentation') }}</a>
        <p class="tiny subtle">{{ t('nav.noPrivilegedChannel') }}</p>
      </div>
    </aside>

    <div v-if="ui.sidebarOpen" class="scrim" @click="ui.closeSidebar()" />

    <div class="main-column">
      <header class="topbar">
        <OButton class="burger" variant="ghost" size="sm" @click="ui.toggleSidebar()">
          <span aria-hidden="true">☰</span>
          <span class="sr-only">{{ t('nav.toggle') }}</span>
        </OButton>

        <div class="grow" />

        <div class="session">
          <span class="small muted truncate">{{ auth.user?.email ?? t('session.signedIn') }}</span>
          <OButton variant="ghost" size="sm" @click="signOut">{{ t('session.signOut') }}</OButton>
        </div>
      </header>

      <main id="main" class="content">
        <slot />
      </main>
    </div>
  </div>
</template>

<style scoped>
.shell {
  display: grid;
  grid-template-columns: var(--sidebar-width) 1fr;
  min-height: 100vh;
  min-height: 100dvh;
}

.skip {
  position: absolute;
  left: -9999px;
  top: var(--space-2);
  z-index: var(--z-modal);
  background: var(--bg-surface);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  border: var(--border-width) solid var(--border-strong);
}

.skip:focus {
  left: var(--space-2);
}

.sidebar {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
  padding: var(--space-4);
  background: var(--bg-surface-sunken);
  border-right: var(--border-width) solid var(--border-default);
  position: sticky;
  top: 0;
  height: 100vh;
  height: 100dvh;
  overflow-y: auto;
}

.brand {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--text-strong);
}

.mark {
  /* Taller than wide (viewBox 338×360), so height leads and width follows. */
  height: 1.6rem;
  width: auto;
  display: block;
}

.wordmark {
  font-size: var(--text-md);
  letter-spacing: -0.01em;
}

.wordmark strong {
  font-weight: var(--weight-bold);
}

nav {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.nav-link {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  color: var(--text-default);
  border-left: 2px solid transparent;
}

.nav-link:hover {
  background: var(--bg-hover);
  text-decoration: none;
}

.nav-link.active {
  background: var(--bg-selected);
  border-left-color: var(--accent);
  color: var(--accent-text);
}

.nav-label {
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
}

.nav-hint {
  font-size: var(--text-2xs);
  color: var(--text-subtle);
}

.sidebar-foot {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.locale select {
  width: 100%;
  background: none;
  border: var(--border-width) solid var(--border-default);
  border-radius: var(--radius-md);
  color: var(--text-muted);
  font-size: var(--text-xs);
  padding: var(--space-2);
  cursor: pointer;
}

.locale select:hover {
  background: var(--bg-hover);
  color: var(--text-strong);
}

.theme {
  background: none;
  border: var(--border-width) solid var(--border-default);
  border-radius: var(--radius-md);
  color: var(--text-muted);
  font-size: var(--text-xs);
  padding: var(--space-2);
  cursor: pointer;
  text-align: left;
}

.theme:hover {
  background: var(--bg-hover);
  color: var(--text-strong);
}

.main-column {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.topbar {
  position: sticky;
  top: 0;
  z-index: var(--z-sticky);
  display: flex;
  align-items: center;
  gap: var(--space-3);
  height: var(--topbar-height);
  padding-inline: var(--space-4);
  background: color-mix(in srgb, var(--bg-canvas) 85%, transparent);
  backdrop-filter: blur(8px);
  border-bottom: var(--border-width) solid var(--border-subtle);
}

.session {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-width: 0;
}

.content {
  flex: 1;
  min-width: 0;
}

.burger {
  display: none;
}

.scrim {
  display: none;
}

@media (max-width: 60rem) {
  .shell {
    grid-template-columns: 1fr;
  }

  .sidebar {
    position: fixed;
    inset: 0 auto 0 0;
    width: var(--sidebar-width);
    z-index: var(--z-drawer);
    transform: translateX(-100%);
    transition: transform var(--duration-base) var(--ease-out);
  }

  .sidebar-open .sidebar {
    transform: translateX(0);
    box-shadow: var(--shadow-lg);
  }

  .sidebar-open .scrim {
    display: block;
    position: fixed;
    inset: 0;
    z-index: calc(var(--z-drawer) - 1);
    background: var(--bg-overlay);
  }

  .burger {
    display: inline-flex;
  }
}
</style>
